import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { currentSession } from '@/api/client'
import { attendreAmorcage } from '@/identity/amorcage'
import { allerALaPorte, annoncerPorteInjoignable } from '@/identity/porte'
import { accueilDuPerimetre, peutUtiliserSuperuser } from '@/navigation'

/**
 * Routes de l'application.
 *
 * Chaque vue est chargée PARESSEUSEMENT (`() => import(...)`) : Vite en fait un
 * fragment séparé, et l'écran de connexion — le seul que voit un visiteur non
 * authentifié — ne télécharge pas le tableau de bord, ses graphiques et ses
 * dépendances.
 *
 * `meta.public` marque les routes accessibles sans session. Tout le reste exige
 * une session : la liste blanche est plus sûre que la liste noire, où un écran
 * ajouté sans `requiresAuth` devient accessible à tous sans qu'on le remarque.
 */
/**
 * Le premier écran du rail — la page d'atterrissage, et le repli des gardes.
 *
 * Lue au moment du besoin et non figée : la session n'existe pas quand ce
 * module est évalué, et les items visibles dépendent du compte.
 *
 * ─── LE REPLI, depuis la disparition du « Portail de services » ─────────────
 *
 * Le portail servait d'atterrissage universel. Il n'existe plus : ses tuiles de
 * lancement faisaient doublon avec la porte du parc, et il ne s'affichait en
 * pratique que par accident — quand le menu était vide faute de session lue.
 *
 * Le repli est désormais « Développeurs », la seule entrée SANS filtre de rôle
 * ni garde d'écran. Un compte sans aucun écran de travail atterrit donc sur une
 * page qui existe pour lui, au lieu d'une redirection qui n'aboutit nulle part.
 */
const REPLI = 'developpeurs'

function accueilApresConnexion(): string {
  const utilisateur = currentSession()?.user ?? null
  const premier = accueilDuPerimetre(utilisateur, 'utilisateur')
  if (premier === null) return REPLI

  // Le registre dit ce que le RAIL montre ; il ne connaît pas les gardes de
  // ROUTE (`hyperadmin`). Envoyer quelqu'un vers un écran que la garde refuse
  // le renverrait ici, qui l'y renverrait — boucle de redirection, mesurée.
  const cible = router.resolve({ name: premier })
  if (cible.meta.hyperadmin === true && utilisateur?.isHyperadmin !== true) return REPLI
  return premier
}

const routes: RouteRecordRaw[] = [
  // Trois écrans PUBLICS d'accès au compte : acceptation d'invitation, demande
  // et réinitialisation de mot de passe. Leurs chemins sont stables par
  // construction — ils sont écrits dans les courriels envoyés (`core/mail/
  // templates.ts`) : renommer l'un d'eux sans toucher le gabarit, et tous les
  // liens déjà partis mènent au 404 de l'attrape-tout.
  {
    path: '/invitations/accept',
    name: 'invitation.accept',
    component: () => import('@/views/InvitationAcceptView.vue'),
    meta: { public: true },
  },
  {
    path: '/mot-de-passe-oublie',
    name: 'password.forgot',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/mot-de-passe/reinitialiser',
    name: 'password.reset',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { public: true },
  },
  // Vérification d'une adresse ajoutée (BBG-65), QUATRIÈME écran public du
  // même régime : son chemin est écrit dans le courriel de preuve de
  // possession (`core/mail/templates.ts`, `/adresse/verifier`) — chemin
  // réservé depuis l'origine pour exactement ce parcours, à ne pas renommer
  // sans toucher le gabarit.
  {
    path: '/adresse/verifier',
    name: 'adresse.verifier',
    component: () => import('@/views/VerifierAdresseView.vue'),
    meta: { public: true },
  },
  // Page d'inscription du backoffice, MORTE volontairement (D5, addendum 5) :
  // l'atterrissage du clic sur une identité NON INSCRITE du changement de
  // compte (widget d'identité) — « les inscriptions sont fermées au public »,
  // aucun formulaire. PUBLIQUE : le compte affiché n'est PAS connecté ici, et
  // la session du compte courant — intacte, l'adaptateur n'a rien basculé —
  // doit pouvoir juste revenir.
  {
    path: '/inscription',
    name: 'inscription',
    component: () => import('@/views/InscriptionView.vue'),
    meta: { public: true },
  },
  // Documentation d'intégration PUBLIQUE : un intégrateur sans compte doit
  // pouvoir la lire avant de décider de s'inscrire. L'onglet actif est porté
  // par l'URL — un lien partagé retombe sur le bon document.
  //
  // `/api` ne dépend QUE du frontend : l'API REST, elle, vit sous `/api/` —
  // le slash final les distingue, et le serveur ne répond que sur des chemins
  // déclarés. Si un jour l'hébergement sert le SPA et l'API derrière le même
  // nom de domaine avec un préfixe, c'est là qu'il faudra déplacer l'API.
  {
    path: '/api',
    name: 'doc.api',
    component: () => import('@/views/DocPubliqueView.vue'),
    meta: { public: true },
  },
  {
    path: '/mcp',
    name: 'doc.mcp',
    component: () => import('@/views/DocPubliqueView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    children: [
      {
        // Préférences et historique des notifications de sécurité (BBG-71).
        // Chemin NEUTRE, sans préfixe « compte » : l'écran de sécurité regroupé
        // (BBG-116) pourra l'absorber sans casser ni lien ni signet. Pas dans
        // le registre de navigation — c'est un écran du TITULAIRE, il vit dans
        // le menu du compte, comme le profil.
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/views/NotificationsView.vue'),
      },
      {
        // Inventaire des identifiants de SON identité (BBG-65). Même régime
        // que les notifications ci-dessus : écran du titulaire, chemin neutre,
        // pas dans le registre de navigation.
        path: 'adresses',
        name: 'adresses',
        component: () => import('@/views/AdressesView.vue'),
      },
      {
        // Sessions et appareils (BBG-13) : les connexions de SON identité, et
        // la coupure ciblée de celle qui n'est pas lui. Troisième écran du
        // titulaire sous le même régime — chemin neutre, menu du compte.
        path: 'sessions',
        name: 'sessions',
        component: () => import('@/views/SessionsView.vue'),
      },
      {
        // « Mon profil » (BBG-116) : l'espace personnel PERMANENT, sur l'identité
        // GLOBALE — il doit exister pour un compte sans organisation et sans
        // application, donc son chemin ne peut porter AUCUN périmètre. Même
        // régime que les trois écrans titulaires ci-dessus : chemin neutre,
        // pas dans le registre de navigation, ouverture par le menu du compte.
        path: 'profil',
        name: 'profil',
        component: () => import('@/views/MonProfilView.vue'),
      },
      {
        // Centre de sécurité (BBG-43) : la vue d'ensemble du titulaire sur la
        // protection de SON compte — niveau, facteurs d'authentification,
        // événements récents. Il MÈNE aux écrans qui agissent (sessions,
        // notifications), il ne les duplique pas. Même régime que les trois
        // écrans du titulaire ci-dessus : chemin neutre, menu du compte.
        path: 'securite',
        name: 'securite',
        component: () => import('@/views/SecuriteView.vue'),
      },
      {
        // Le périmètre plateforme a son propre préfixe : l'URL dit dans quel
        // périmètre on se trouve, ce qui compte quand on colle un lien dans un
        // ticket.
        // PAS sous le préfixe `superuser/` : l'annuaire des identités est le
        // métier du produit, pas un réglage de la plateforme. Il reste
        // néanmoins réservé à l'organisation de gestion — les identités n'ont
        // pas d'`organisation_id` (ADR-001), donc « celles de cette
        // organisation » n'existe pas. D'où `hyperadmin` et non `scope`.
        /*
         * LA RACINE EST UN ALIAS DE CET ÉCRAN, pas une redirection vers lui.
         *
         * Une porte d'écosystème s'ouvre sur son DOMAINE : `backacces.betool.fr`
         * doit afficher l'application, pas `backacces.betool.fr/identites`. Une
         * redirection ajouterait un segment que rien ne justifie, et qui se
         * retrouverait dans chaque signet et chaque lien partagé.
         *
         * Un `redirect` était en outre impossible à calculer juste : il
         * s'évalue pendant la RÉSOLUTION de la route, donc avant la garde,
         * donc avant que l'échange du laissez-passer ait ouvert la session. La
         * destination se calculait sur une session vide, et l'atterrissage
         * tombait sur le repli. Mesuré deux fois en production.
         *
         * L'alias porte le MÊME nom de route : la barre latérale marque donc
         * « Identités » comme actif à la racine aussi. Qui n'a pas le droit
         * d'entrer ici est renvoyé par la garde vers son propre accueil — le
         * `meta.hyperadmin` ci-dessous vaut pour les deux adresses.
         */
        path: 'identites',
        alias: '',
        name: 'identites',
        component: () => import('@/views/IdentitesView.vue'),
        meta: { hyperadmin: true },
      },
      {
        // Même périmètre que l'annuaire des identités, et pour la même raison :
        // le registre n'a pas d'`organisation_id` (une application sert
        // plusieurs organisations), donc « celles de cette organisation »
        // n'existe pas. D'où `hyperadmin` et non `scope`.
        // Le CATALOGUE de ce qu'on sait déployer. Même garde que les
        // applications, et pour la même raison : il décrit le parc, qui
        // n'appartient à aucun locataire.
        path: 'logiciels',
        name: 'logiciels',
        component: () => import('@/views/LogicielsView.vue'),
        meta: { hyperadmin: true },
      },
      {
        path: 'applications',
        name: 'applications',
        component: () => import('@/views/ApplicationsView.vue'),
        meta: { hyperadmin: true },
      },
      {
        path: 'superuser/organisations',
        name: 'superuser.organisations',
        component: () => import('@/views/SuperuserOrganisationsView.vue'),
        meta: { scope: 'superuser' },
      },
      {
        path: 'superuser/plans',
        name: 'superuser.plans',
        component: () => import('@/views/SuperuserPlansView.vue'),
        meta: { scope: 'superuser' },
      },
      {
        path: 'superuser/services',
        name: 'superuser.services',
        component: () => import('@/views/SuperuserServicesView.vue'),
        meta: { scope: 'superuser' },
      },
      {
        // Les comptes de FOURNISSEUR de l'organisation (migration 0114) : ce
        // sont ses identifiants, pas un réglage de la plateforme — d'où
        // l'absence de `scope: superuser`. La garde réelle est la capacité
        // backoffice `connecteurs.consulter`, côté serveur.
        path: 'comptes',
        name: 'comptes',
        component: () => import('@/views/ComptesView.vue'),
      },
      {
        // En dernier des enfants : la documentation est un recours, là où on
        // la cherche quand le reste n'a pas suffi.
        path: 'developpeurs',
        name: 'developpeurs',
        component: () => import('@/views/DevelopersView.vue'),
      },
    ],
  },
  {
    // Attrape-tout : une URL inconnue montre un écran explicite plutôt qu'une
    // page blanche, qui est indiscernable d'un plantage du bundle.
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  // Retour en haut de page à chaque navigation, sauf quand le navigateur
  // restitue une position (bouton Précédent) : on respecte alors son choix.
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

/**
 * Garde d'accès. Elle lit la SESSION PERSISTÉE et non le store Pinia : la garde
 * s'exécute avant le premier montage de composant, moment où le store peut ne
 * pas être hydraté. Le store reste la source de vérité pour l'affichage.
 */
router.beforeEach(async (to) => {
  // En mode trousseau, la session d'une page rechargée est RETROUVÉE, pas lue :
  // le slot actif et le cookie scellé la portent, et l'amorçage (BBG-233) va
  // les chercher pendant que la page se peint. Trancher sans l'attendre
  // enverrait vers l'écran de connexion une personne parfaitement connectée —
  // et l'écran disparaîtrait aussitôt, ce « flash » que les utilisateurs
  // rapportent comme un bug. En mode bearer, cette attente est résolue
  // d'avance : aucune latence ajoutée.
  await attendreAmorcage()

  const authenticated = currentSession() !== null
  const session = currentSession()

  if (!to.meta.public && !authenticated) {
    // `redirect` : après connexion, on renvoie l'utilisateur là où il allait.
    // Sans ça, un lien profond partagé par courriel retombe toujours sur
    // l'accueil, et l'utilisateur doit re-naviguer à la main.
    /*
     * `redirect` n'est posé QUE s'il apprend quelque chose.
     *
     * Vers la racine, il ne dit rien — c'est déjà la destination par défaut
     * après connexion — et il laisse un paramètre dans la barre d'adresse, donc
     * dans l'historique et dans tout lien copié. Une porte d'écosystème répond
     * à deux adresses, `/login` et la racine : pas à `/login?redirect=/`.
     */
    /*
     * À LA PORTE, et non vers un écran d'ici : cette application n'en a plus.
     *
     * `redirect` n'a plus de destinataire non plus — la porte compare
     * l'ORIGINE aux points d'entrée enregistrés, jamais un chemin, et le
     * laissez-passer ramène ici de toute façon.
     *
     * On ANNULE la navigation (`false`) : la page part, et laisser le routeur
     * peindre une destination qu'on quitte ferait clignoter un écran vide.
     */
    if (!allerALaPorte()) annoncerPorteInjoignable()
    return false
  }


  // --- Habilitations d'ÉCRAN -------------------------------------------------
  //
  // Le registre de navigation cache les entrées interdites ; cette garde refuse
  // l'ENTRÉE. Les deux sont nécessaires et ne font pas le même travail : un lien
  // profond, un signet ou une URL tapée à la main n'passent pas par le menu. Le
  // serveur reste l'autorité — ces contrôles évitent d'afficher un écran qui se
  // remplirait d'erreurs 403, ils ne protègent aucune donnée.
  const utilisateur = session?.user ?? null

  /*
   * REPLI SUR L'ACCUEIL, et il se DÉRIVE.
   *
   * L'écran d'atterrissage universel était le portail ; il n'existe plus.
   * `accueilApresConnexion` rend le premier écran réellement ouvert au compte —
   * et « Développeurs » quand il n'y en a aucun. Écrire un nom en dur ici
   * rouvrirait la boucle qu'on a déjà mesurée : un refus qui renvoie vers un
   * écran lui-même refusé.
   */
  if (to.meta.scope === 'superuser' && !peutUtiliserSuperuser(utilisateur)) {
    return { name: accueilApresConnexion() }
  }

  // Même repli, pour les écrans de TRAVAIL réservés à l'organisation de
  // gestion : ils ne sont pas dans le périmètre plateforme, mais un lien
  // profond ou un signet doit y renvoyer aussi proprement.
  if (to.meta.hyperadmin === true && !peutUtiliserSuperuser(utilisateur)) {
    return { name: accueilApresConnexion() }
  }

  const rolesRequis = to.meta.roles as readonly string[] | undefined
  if (rolesRequis !== undefined && !rolesRequis.includes(utilisateur?.role ?? '')) {
    return { name: accueilApresConnexion() }
  }

  return true
})
